import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { UserApi, LoginApi, LogoutApi, WishlistApi, RegisterApi, SubscriptionApi } from "../api";
import { useAppStore } from "./AppStore";
import { safeStorage } from "./ssrStorage";

type InitialState = {
	userInfo: Record<string, any>;
	wishlistInfo: Record<string, any>;
	subscriptions: Array<any>;
	loginFailed: boolean;
};

const initialState = {
	userInfo: {},
	wishlistInfo: {},
	subscriptions: [],
	loginFailed: false,
};

interface UserState extends InitialState {
	GetInfo: () => Promise<Record<string, any>>;
	Register: (data: Record<string, any>) => Promise<void>;
	Login: (email: string, password: string) => Promise<Record<string, any>>;
	SocialLogin: (provider: string, code: string) => Promise<Record<string, any>>;
	ForgotPassword: (email: string) => Promise<boolean>;
	UpdatePassword: (token: string, password: string) => Promise<boolean>;
	CheckPasswordToken: (token: string) => Promise<boolean>;
	Update: (data: Record<string, any>) => Promise<void>;
	Logout: () => Promise<Record<string, any>>;
	WishlistGet: () => Promise<Record<string, any>>;
	WishlistAdd: (data: Record<string, any>) => Promise<void>;
	WishlistRemove: (data: Record<string, any>) => Promise<void>;
	LoyaltyOptIn: (user_id: string) => Promise<Record<string, any>>;
	GetSubscriptions: () => Promise<void>;
	CancelSubscription: (id: string) => Promise<void>;
	PauseSubscription: (id: string) => Promise<void>;
	ResumeSubscription: (id: string) => Promise<void>;
}

const useUserStore = create<UserState>()(
	persist(
		devtools((set, get) => ({
			...initialState,
			async GetInfo() {
				try {
					const response = await UserApi.getInfo();

					if (typeof response === "object" && Object.keys(response).length > 0) {
						set({ userInfo: response.user_info });

						if (useAppStore.getState().isDebug) {
							console.log(`UserStore.js - User info`, response.user_info);
						}

						try {
							if (typeof klaviyo !== "undefined" && typeof klaviyo.identify === "function") {
								klaviyo.identify({
									email: response.user_info?.email ?? "",
								});
							}
						} catch (error) {
							console.log("Failed to identify user:", error);
						}

						get().WishlistGet();
						return response.user_info;
					}

					set({ userInfo: {} });
					return {};
				} catch (error) {
					// console.error("Failed to get user info:", error);
					set({ userInfo: {} });
					return {};
				}
			},
			async Register(data) {
				const response = await RegisterApi.Register(data);

				if (useAppStore.getState().isDebug) {
					console.log(`UserStore.js - Register user`, response);
				}

				get().GetInfo();
			},
			async Login(email, password) {
				try {
					const response = await LoginApi.Login(email, password);

					if (typeof response === "object" && Object.keys(response).length === 0) {
						console.error("login failed");
						set({ loginFailed: true });
						return response;
					} else {
						if (useAppStore.getState().isDebug) {
							console.log(`UserStore.js - Login success`);
						}

						set({ userInfo: response.user_info, loginFailed: false });
						return response.user_info;
					}
				} catch (error) {
					console.error("Failed to login:", error);
					return {};
				}
			},
			async SocialLogin(provider, code) {
				try {
					const response = await LoginApi.SocialLogin(provider, code);

					if (typeof response === "object" && Object.keys(response).length === 0) {
						console.error("login failed");
						set({ loginFailed: true });
						return response;
					} else {
						if (useAppStore.getState().isDebug) {
							console.log(`UserStore.js - Social login success`);
						}

						set({ userInfo: response.user_info, loginFailed: false });
						return response.user_info;
					}
				} catch (error) {
					console.error("Failed to social login:", error);
					return {};
				}
			},
			async ForgotPassword(email) {
				try {
					const response = await LoginApi.ForgotPassword(email);
					if (response.success && response.success === true) {
						return true;
					}
				} catch (error) {
					console.error(error);
					return error;
				}
			},
			async UpdatePassword(token, password) {
				try {
					const response = await LoginApi.UpdatePassword(token, password);
					if (response.success && response.success === true) {
						return true;
					}
				} catch (error) {
					console.error(error);
					return error;
				}
			},
			async CheckPasswordToken(token) {
				try {
					const response = await LoginApi.CheckPasswordToken(token);
					if (response.success && response.success === true) {
						return true;
					}
				} catch (error) {
					console.error(error);
					return error;
				}
			},
			async Update(data) {
				await UserApi.update(data);

				if (useAppStore.getState().isDebug) {
					console.log(`UserStore.js - Updating user`, data);
				}

				get().GetInfo();
			},
			async Logout() {
				try {
					const response = await LogoutApi.Logout();
					set({ userInfo: {} });
					useAppStore.getState().clearSessionId();
					return response;
				} catch (error) {
					console.error("Failed to logout:", error);
					return {};
				}
			},
			async WishlistGet() {
				try {
					const response = await WishlistApi.get();

					let wishlistInfo = {};
					if (response && response.wishlist) {
						response.wishlist.forEach((item) => {
							wishlistInfo[item.product_id] = item;
						});
					}

					set({ wishlistInfo: wishlistInfo });
					return wishlistInfo;
				} catch (error) {
					console.error("Failed to get wishlist:", error);
					return {};
				}
			},
			async WishlistAdd(data) {
				try {
					if (!data.product_id) {
						throw new Error("No product id");
					}
					await WishlistApi.add(data);
					await get().WishlistGet();
				} catch (error) {
					console.error("Failed to add to wishlist:", error);
					return {};
				}
			},
			async LoyaltyOptIn(user_id) {
				try {
					const response = await UserApi.LoyaltyOptIn(user_id);
					return response;
				} catch (error) {
					console.error("Failed to add to loyalty scheme.");
					return {};
				}
			},
			async WishlistRemove(data) {
				try {
					if (!data.product_id) {
						throw new Error("No product id");
					}
					await WishlistApi.remove(data);
					await get().WishlistGet();
				} catch (error) {
					console.error("Failed to remove from wishlist:", error);
					return {};
				}
			},
			async GetSubscriptions() {
				try {
					const { subscriptions } = await SubscriptionApi.getSubscriptions();
					set({ subscriptions });
				} catch (error) {
					console.error("Failed to get subscriptions:", error);
					return {};
				}
			},
			async CancelSubscription(id) {
				try {
					await SubscriptionApi.cancelSubscription(id);
					await get().GetSubscriptions();
				} catch (error) {
					console.error("Failed to cancel subscription:", error);
					return {};
				}
			},
			async PauseSubscription(id) {
				try {
					await SubscriptionApi.pauseSubscription(id);
					await get().GetSubscriptions();
				} catch (error) {
					console.error("Failed to pause subscription:", error);
					return {};
				}
			},
			async ResumeSubscription(id) {
				try {
					await SubscriptionApi.resumeSubscription(id);
					await get().GetSubscriptions();
				} catch (error) {
					console.error("Failed to resume subscription:", error);
					return {};
				}
			},
		})),
		{
			name: "user-storage",
			storage: safeStorage,
		},
	),
);

export { useUserStore };
